import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, Image, Plus, AlertCircle, X } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import FileProcessingService from '../services/FileProcessingService';

const MAX_IMAGE_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_PDF_FILE_SIZE = 500 * 1024 * 1024; // 500MB
const MAX_FILES_PER_UPLOAD = 50;

const FileUploadArea = ({ 
  collection, 
  onFilesAdded,
  disabled = false,
  className = "",
  userInfo
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    if (!file) return 'Invalid file';

    // Auto-detect project type based on file
    const isPdf = file.type === 'application/pdf';
    const isImage = file.type.startsWith('image/') || file.type === 'image/tiff';

    if (!isPdf && !isImage) {
      return 'Only PDF and image files (JPEG, PNG, TIF) are supported';
    }

    if (isPdf && file.size > MAX_PDF_FILE_SIZE) {
      return `PDF files must be less than ${MAX_PDF_FILE_SIZE / (1024 * 1024)}MB`;
    }

    if (isImage && file.size > MAX_IMAGE_FILE_SIZE) {
      return `Image files must be less than ${MAX_IMAGE_FILE_SIZE / (1024 * 1024)}MB`;
    }

    return null;
  };

  const handleFileSelect = useCallback((files) => {
    if (disabled) return;
    
    setError('');
    const fileArray = Array.from(files);

    // Limit number of files per upload
    if (fileArray.length > MAX_FILES_PER_UPLOAD) {
      setError(`You can upload a maximum of ${MAX_FILES_PER_UPLOAD} files at once`);
      return;
    }

    // Validate all files
    const validFiles = [];
    const errors = [];

    for (const file of fileArray) {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    if (validFiles.length > 0) {
      // Add files directly to processing queue using the service
      if (collection && userInfo) {
        try {
          // Determine file type for each file
          const hasImages = validFiles.some(f => f.type.startsWith('image/'));
          const hasPdfs = validFiles.some(f => f.type === 'application/pdf');
          const detectedType = hasPdfs ? 'pdf' : 'snippet';
          
          FileProcessingService.addFilesToQueue(
            validFiles, 
            detectedType, 
            collection._id, 
            userInfo
          );
          
          // Clear any previous errors
          setError('');
          
          // Notify parent if callback provided
          onFilesAdded?.(validFiles, detectedType);
        } catch (error) {
          setError(`Failed to start upload: ${error.message}`);
        }
      } else {
        // Fallback for when collection or userInfo not available
        onFilesAdded?.(validFiles, 'auto');
      }
    }
  }, [disabled, onFilesAdded]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;

    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  const handleFileInput = (e) => {
    if (disabled) return;
    const files = e.target.files;
    handleFileSelect(files);
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const clearError = () => setError('');

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start justify-between">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
          </div>
          <button 
            onClick={clearError}
            className="text-red-400 hover:text-red-600 ml-2"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Upload Area */}
      <Card 
        className={`relative transition-all duration-200 cursor-pointer border-2 border-dashed
          ${dragActive 
            ? 'border-blue-500 bg-blue-50/50 dark:bg-blue-900/20' 
            : 'border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onClick={handleClick}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <CardContent className="p-8 text-center">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,image/*"
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled}
          />

          <div className="flex flex-col items-center space-y-4">
            {/* Icon */}
            <div className={`w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
              dragActive 
                ? 'bg-blue-100 dark:bg-blue-900/40' 
                : 'bg-slate-100 dark:bg-slate-800'
            }`}>
              {dragActive ? (
                <Upload className="h-8 w-8 text-blue-500" />
              ) : (
                <Plus className="h-8 w-8 text-slate-500" />
              )}
            </div>

            {/* Title */}
            <div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-1">
                {dragActive ? 'Drop files here' : 'Add files to collection'}
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {dragActive ? 
                  'Release to upload files' : 
                  'Drag & drop or click to upload PDF or image files'
                }
              </p>
            </div>

            {/* File type indicators */}
            {!dragActive && (
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <div className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  <span>PDF</span>
                </div>
                <div className="flex items-center gap-1">
                  <Image className="h-4 w-4" />
                  <span>Images</span>
                </div>
              </div>
            )}

            {/* Collection info */}
            {collection && (
              <div className="text-xs text-slate-400">
                Adding to: <span className="font-medium text-slate-500 dark:text-slate-300">{collection.name}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FileUploadArea;